import { useRef, useEffect, useState } from 'react';
import { YoutubeLogo, SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';

export default function VideoEmbed({ video }) {
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [muted, setMuted] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);

  // CRITICAL: iframe mount cascade prevention.
  // YouTube iframes call scrollIntoView() on load when autoplay=1. Combined with
  // `scroll-snap-type: y mandatory`, this triggers a cascade: video A mounts →
  // scrolls itself into view → snaps to video B → B mounts → scrolls → snap to C, etc.
  // Fix: only mount the iframe after the item has been ≥60% visible for 350ms.
  // Fast scroll passes (<350ms intersection) never mount anything, killing the cascade.
  useEffect(() => {
    if (!containerRef.current) return;
    let stableTimer = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          // Schedule mount, but only if still stable after debounce window
          stableTimer = setTimeout(() => setVisible(true), 350);
        } else {
          // Cancel any pending mount; unmount immediately when leaving
          if (stableTimer) {
            clearTimeout(stableTimer);
            stableTimer = null;
          }
          setVisible(false);
        }
      },
      { threshold: [0, 0.6, 0.9] }
    );
    observer.observe(containerRef.current);
    return () => {
      if (stableTimer) clearTimeout(stableTimer);
      observer.disconnect();
    };
  }, []);

  const isYouTube = video.platform === 'youtube';
  const isTikTok = video.platform === 'tiktok';

  const getEmbedUrl = () => {
    if (isYouTube) {
      return `https://www.youtube.com/embed/${video.video_id}?autoplay=1&mute=${muted ? 1 : 0}&loop=1&playsinline=1&controls=0&rel=0&playlist=${video.video_id}&modestbranding=1&showinfo=0`;
    }
    return video.embed_url;
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setMuted(prev => !prev);
    setIframeKey(prev => prev + 1);
  };

  return (
    <div
      ref={containerRef}
      className="video-reel-item"
      data-testid={`video-embed-${video.video_id}`}
    >
      {/* Thumbnail shown when video is NOT active */}
      {!visible && video.thumbnail && (
        <img src={video.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 1 }} />
      )}
      {!visible && !video.thumbnail && (
        <div className="absolute inset-0 bg-black" style={{ zIndex: 1 }} />
      )}

      {/* iframe ONLY rendered when this item is the visible snap target */}
      {visible && isYouTube && (
        <iframe
          key={iframeKey}
          src={getEmbedUrl()}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{ border: 'none', zIndex: 2, pointerEvents: 'none' }}
          title={video.title}
        />
      )}

      {visible && isTikTok && (
        <iframe
          src={video.embed_url}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; encrypted-media"
          allowFullScreen
          style={{ border: 'none', zIndex: 2, pointerEvents: 'none' }}
          title={video.title}
        />
      )}

      {/* Mute toggle */}
      {visible && isYouTube && (
        <button
          onClick={toggleMute}
          className="absolute top-4 right-4 glass-pill rounded-full p-2.5 text-white hover:bg-white/20 transition-colors"
          style={{ zIndex: 20 }}
          data-testid={`video-mute-${video.video_id}`}
        >
          {muted ? <SpeakerSlash size={18} /> : <SpeakerHigh size={18} />}
        </button>
      )}

      {/* Bottom overlay — always visible for context */}
      <div className="feed-overlay" style={{ zIndex: 10, pointerEvents: 'none' }}>
        <div className="flex items-center gap-1.5 mb-1">
          {isYouTube && <YoutubeLogo size={14} weight="fill" className="text-red-500" />}
          {isTikTok && (
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current text-white" aria-hidden="true">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78c.27 0 .54.04.8.1v-3.5a6.37 6.37 0 0 0-.8-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 10.86 4.43V13a8.26 8.26 0 0 0 4.81 1.52h.77V11.1a4.79 4.79 0 0 1-3.77-1.52V6.7h3.77z" />
            </svg>
          )}
          <span className="text-white/70 text-[10px] font-sans font-medium" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
            {video.channel}
          </span>
        </div>
        <p className="text-white text-xs font-sans font-semibold line-clamp-2 leading-snug" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>
          {video.title}
        </p>
      </div>
    </div>
  );
}
